<template>
  <div>
    <div>Resultados</div>
    <div
      class="d-flex mt-4 flex-row"
      :class="{
        'flex-column gap-8': $vuetify.breakpoint.smAndDown,
        'gap-12': !$vuetify.breakpoint.smAndDown,
      }"
    >
      <div v-for="indicator in indicators" :key="indicator.key">
        <div>
          {{ indicator.name }}
          <v-icon v-if="indicator.key === 'easi'" @click="showImageDialog = true">mdi-information-outline</v-icon>
        </div>
        <div></div>
        <div class="d-flex align-center mt-2 gap-2" :class="{ 'disabled-node': disabled }">
          <v-text-field
            outlined
            dense
            :min="0"
            :max="indicator.max"
            type="number"
            v-if="form.easi_bsa.resultado_manual"
            v-model="form.easi_bsa[indicator.key]"
            :rules="[$validation.minValue(0), $validation.maxValue(indicator.max)]"
            :disabled="disabled"
            hide-spin-buttons
          />
          <span
            v-else
            :class="{
              'red--text': indicator.getComputedValue() > 9,
              'black--text text-h6': indicator.getComputedValue() == null,
              'big-number': indicator.getComputedValue() != null,
            }"
          >
            {{ indicator.getComputedValue() == null ? 'Indefinido' : indicator.getComputedValue() }}</span
          >
          <span
            v-if="indicator.getComputedValue()"
            class="ml-2"
            :class="{ 'red--text': indicator.getComputedValue() > 9 }"
            style="width: 120px"
          >
            {{ indicator.getComputedValue() > 9 ? 'Moderado a grave' : 'Leve' }}</span
          >
        </div>
      </div>
    </div>
    <v-switch
      class="my-4"
      label="Aplicar resultado manualmente"
      v-model="form.easi_bsa.resultado_manual"
      :disabled="disabled"
      hide-details
    />
    <area-slider
      :data="form.easi_bsa"
      :area="areas"
      :types="skinProblemTypes"
      :max="4"
      :class="{ 'disabled-node': disabled }"
      v-if="!form.easi_bsa.resultado_manual"
    />

    <div>
      <div>
        EASI
        <v-icon @click="showImageDialog = true">mdi-information-outline</v-icon>
      </div>
      <div class="rule my-4">Rule of 9's</div>
      <v-img src="/assets/attendance-form/easi.png" alt="" max-width="589" />
      <p class="reference mt-4">
        Referência: Heaf, James G. "The origin of the 1· 73‐m2 body surface area normalization: problems and
        implications." Clinical physiology and functional imaging 27.3 (2007): 135-137.
      </p>
    </div>

    <v-dialog v-model="showImageDialog" max-width="800px">
      <v-img src="/assets/easi.png" max-width="800px" height="100%" />
    </v-dialog>
  </div>
</template>
<script>
import AreaSlider from '@/components/AttendanceForm/general/phase/AreaSlider.vue'
import { createNamespacedHelpers } from 'vuex'
import { cloneDeep } from 'lodash'

const { mapActions: newAttendanceFormActions, mapMutations: newAttendanceFormMutations } =
  createNamespacedHelpers('newAttendanceForm')

export default {
  name: 'EasiBsaParameterNode',
  components: { AreaSlider },
  async created() {
    const { questions } = (await this.getParameter('atopic-dermatitis:easi-bsa')) ?? {}
    if (questions) {
      this.form = cloneDeep(questions)
    }
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        easi_bsa: {
          easi: null,
          bsa: null,
          resultado_manual: false,
          cabeca_pescoco: {
            eritema: null,
            edema: null,
            liquenificacao: null,
            area: null,
          },
          tronco: {
            eritema: null,
            edema: null,
            liquenificacao: null,
            area: null,
          },
          membros_superiores: {
            eritema: null,
            edema: null,
            liquenificacao: null,
            area: null,
          },
          membros_inferiores: {
            eritema: null,
            edema: null,
            liquenificacao: null,
            area: null,
          },
        },
      },
      showImageDialog: false,
    }
  },
  computed: {
    indicators() {
      return [
        {
          key: 'easi',
          name: 'EASI',
          max: 72,
          getComputedValue: () => this.computedEasi,
        },
        {
          key: 'bsa',
          name: 'BSA',
          max: 100,
          getComputedValue: () => this.computedBsa,
        },
      ]
    },
    skinProblemTypes() {
      return [
        { key: 'eritema', name: 'Eritema' },
        { key: 'descamacao', name: 'Descamação' },
        { key: 'infiltracao', name: 'Infiltração' },
      ]
    },
    areas() {
      return [
        {
          key: 'cabeca_pescoco',
          name: 'Cabeça e pescoço',
          factor: 0.1,
          childrenFactor: 0.2,
        },
        {
          key: 'tronco',
          name: 'Tronco',
          factor: 0.3,
        },
        {
          key: 'membros_superiores',
          name: 'Membros superiores',
          factor: 0.2,
        },
        {
          key: 'membros_inferiores',
          name: 'Membros inferiores',
          factor: 0.4,
          childrenFactor: 0.3,
        },
      ]
    },
    computedBsa() {
      if (this.form.easi_bsa.resultado_manual) {
        return this.form.easi_bsa.bsa || 0
      }

      let bsaResult = 0
      for (let areaObject of this.areas) {
        let area = this.form.easi_bsa[areaObject.key]
        bsaResult += area.area * areaObject.factor
      }
      return (Math.round(100 * bsaResult) / 100).toFixed(2)
    },
    computedEasi() {
      if (this.form.easi_bsa.resultado_manual) {
        return this.form.easi_bsa.easi || 0
      }

      let easiResult = 0
      for (let areaObject of this.areas) {
        let area = this.form.easi_bsa[areaObject.key]
        let affectedAreaWeight = this.getAffectedAreaWeight(area.area)

        if (!!area.descamacao || !!area.infiltracao) {
          let newAreaValues = {
            eritema: area.eritema,
            liquenificacao: area.liquenificacao,
          }

          if (!!area.descamacao) {
            newAreaValues.edema = area.descamacao
          }
          area = newAreaValues
        }
        if (areaObject.childrenFactor && !this.isPatientAdult) {
          easiResult +=
            (area.eritema + area.edema + area.liquenificacao) * areaObject.childrenFactor * affectedAreaWeight
        } else {
          easiResult += (area.eritema + area.edema + area.liquenificacao) * areaObject.factor * affectedAreaWeight
        }
      }
      return (Math.round(100 * easiResult) / 100).toFixed(2)
    },
  },
  methods: {
    ...newAttendanceFormActions(['getParameter']),
    ...newAttendanceFormMutations(['updateParameter']),
    getAffectedAreaWeight(value) {
      if (value === 0) {
        return 0
      } else if (value < 10) {
        return 1
      } else if (value >= 10 && value < 30) {
        return 2
      } else if (value >= 30 && value < 50) {
        return 3
      } else if (value >= 50 && value < 70) {
        return 4
      } else if (value >= 70 && value < 90) {
        return 5
      } else {
        return 6
      }
    },
  },
  watch: {
    computedEasi(newVal) {
      if (this.form.easi_bsa.resultado_manual) {
        return
      }
      this.form.easi_bsa.easi = Number(newVal).toFixed(2)
    },
    computedBsa(newVal) {
      if (this.form.easi_bsa.resultado_manual) {
        return
      }
      this.form.easi_bsa.bsa = Number(newVal).toFixed(2)
    },
    form: {
      handler(value) {
        this.updateParameter({
          parameter: 'atopic-dermatitis:easi-bsa',
          value: { score: { easi: this.computedEasi, bsa: this.computedBsa }, questions: value },
        })
      },
      deep: true,
    },
  },
}
</script>
<style scoped>
.big-number {
  font-weight: 500;
  color: green;
  font-size: 2.5rem;
}

.table div {
  align-items: center;
}

.table div:nth-child(1n + 2) {
  border: solid 1px rgba(0, 0, 0, 0.7);
  margin-right: -1px;
  margin-bottom: -1px;
}

.table div:first-child {
  justify-content: flex-end;
}

.table div div {
  height: 100px;
}

.rate-ten > div {
  align-self: center;
  width: fit-content;
  border: 1px solid black;
  padding: 12px 24px;
}

.reference {
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
}

.rule {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}
</style>
